<template>
  <section v-if="visible" ref="fsRoot" class="maquina-estado-v1-root">
    <div class="toolbar-row">
      <div class="toolbar-left">
        <div class="toolbar-title">
          <v-icon size="20" color="#0f5f97">mdi-monitor-dashboard</v-icon>
          Estado de Maquinas NYX
        </div>
        <div v-if="!loading" class="machine-count-pill">{{ maquinasFiltradas.length }} maquinas</div>
        <div v-if="totalPages > 1" class="page-pill">Pantalla {{ currentPageHuman }} / {{ totalPages }}</div>
      </div>
      <button class="fs-btn" @click="extenderPantalla">
        <v-icon size="16" class="mr-1">{{ fsIcon }}</v-icon>
        <span class="fs-btn-label">{{ fsLabel }}</span>
      </button>
    </div>

    <v-card elevation="0" class="dashboard-card">
      <v-card-text class="dashboard-body">
        <v-autocomplete
          v-model="idArea"
          :items="areas"
          label="Area"
          item-value="idArea"
          item-text="nombre"
          hide-details
          clearable
          outlined
          dense
          class="area-select mb-4"
        />

        <div class="filter-chips mb-5">
          <button
            v-for="(filtro, idx) in filtros"
            :key="'filtro-' + idx"
            class="filter-chip"
            :class="{ active: filtroValor === idx }"
            :style="filtroValor === idx ? activeChipStyle(filtro.opcion) : {}"
            @click="filtroValor = idx"
          >
            <span v-if="filtro.opcion !== null" class="chip-dot" :style="{ background: colorCard(filtro.opcion) }"></span>
            {{ filtro.nombre }}
            <span class="chip-count">{{ contarFiltro(filtro.opcion) }}</span>
          </button>
        </div>

        <div v-if="loading && maquinas.length === 0" class="loading-state">
          <v-progress-circular :size="64" :width="5" color="primary" indeterminate />
          <p class="loading-text">Cargando maquinas...</p>
        </div>

        <v-row v-if="visible && (!loading || maquinas.length > 0)" justify="start" align="stretch">
          <v-col
            v-for="maquina in maquinasVisibles"
            :key="'maq-' + maquina.idMaquinaIns"
            cols="12"
            sm="6"
            md="4"
            lg="2"
            xl="2"
            class="d-flex"
          >
            <CardMaquinaNyx :item="maquina" class="w-100" />
          </v-col>
        </v-row>

        <div v-if="!loading && !maquinasFiltradas.length" class="empty-state">
          <v-icon size="32" color="#8a95a3">mdi-information-outline</v-icon>
          <p>No hay maquinas para mostrar con ese filtro.</p>
        </div>
        <v-progress-linear v-if="loading && maquinas.length > 0" indeterminate color="#0f5f97" rounded class="loading-bottom" />
      </v-card-text>
    </v-card>
  </section>
</template>

<script>
import axios from "axios";
import moment from "moment";
import CardMaquinaNyx from "./MaquinaEstado/CardMaquinaNyx.vue";

const STATUS_COLORS = { 0: "#8A95A3", 1: "#00A86B", 3: "#F59E0B", 4: "#E53935" };
const PAGE_SIZE = 12;
const PAGE_ROTATE_MS = 30000;
const DASH_REFRESH_MS = 300000;

export default {
  name: "EstadoMaquinaNyx",
  components: { CardMaquinaNyx },
  inheritAttrs: false,
  props: {
    value: { type: Boolean, default: undefined },
    active: { type: Boolean, default: undefined },
    pollMs: { type: Number, default: DASH_REFRESH_MS },
  },
  data: () => ({
    isFullscreen: false,
    loading: false,
    maquinas: [],
    instancias: [],
    turnos: [],
    areas: [],
    idArea: -1,
    filtroValor: 0,
    interval: null,
    pageRotateInterval: null,
    currentPage: 0,
    tokensCache: {},
    sinDisponibilidad: false,
    desConParosNP: true,
    filtros: [
      { opcion: null, nombre: "Todas" },
      { opcion: 1, nombre: "Activas" },
      { opcion: 3, nombre: "Paro Programado" },
      { opcion: 4, nombre: "Paro No Programado" },
      { opcion: 0, nombre: "Inactivas" },
    ],
  }),
  computed: {
    visible() {
      if (typeof this.active === "boolean") return this.active;
      if (typeof this.value === "boolean") return this.value;
      return true;
    },
    fsLabel() { return this.isFullscreen ? "Salir de pantalla" : "Pantalla completa"; },
    fsIcon() { return this.isFullscreen ? "mdi-arrow-collapse-all" : "mdi-arrow-expand-all"; },
    maquinasFiltradas() {
      const statusOrder = { 1: 0, 3: 1, 4: 2, 0: 3 };
      const opcion = this.filtros[this.filtroValor]?.opcion;
      const filtered = this.maquinas.filter((m) => (opcion == null ? true : m.status === opcion));
      if (opcion != null) return filtered;
      return filtered.slice().sort((a, b) => {
        const aOrder = statusOrder[a.status] ?? 99;
        const bOrder = statusOrder[b.status] ?? 99;
        if (aOrder !== bOrder) return aOrder - bOrder;
        return String(a.codigoMaquina || "").localeCompare(String(b.codigoMaquina || ""));
      });
    },
    maquinaPages() {
      const pages = [];
      for (let i = 0; i < this.maquinasFiltradas.length; i += PAGE_SIZE) {
        pages.push(this.maquinasFiltradas.slice(i, i + PAGE_SIZE));
      }
      return pages.length ? pages : [[]];
    },
    totalPages() { return this.maquinaPages.length; },
    maquinasVisibles() {
      return this.maquinaPages[this.currentPage] || this.maquinaPages[0] || [];
    },
    currentPageHuman() { return Math.min(this.currentPage + 1, this.totalPages); },
    maquinasActivasVisibles() { return this.maquinasVisibles.filter((m) => m.status === 1); },
  },
  watch: {
    visible(n) { n ? this.iniciar() : this.detener(); },
    idArea() { this.currentPage = 0; this.refreshDashboard(); },
    filtroValor() { this.currentPage = 0; this.syncPageRotation(); },
    maquinasFiltradas() {
      if (this.currentPage >= this.totalPages) this.currentPage = 0;
      this.syncPageRotation();
    },
  },
  mounted() {
    if (this.visible) this.iniciar();
    const sync = () => {
      this.isFullscreen = !!(document.fullscreenElement || document.webkitFullscreenElement || document.msFullscreenElement);
    };
    document.addEventListener("fullscreenchange", sync);
    document.addEventListener("webkitfullscreenchange", sync);
    document.addEventListener("MSFullscreenChange", sync);
    this._syncFs = sync;
  },
  beforeDestroy() {
    this.detener();
    if (this._syncFs) {
      document.removeEventListener("fullscreenchange", this._syncFs);
      document.removeEventListener("webkitfullscreenchange", this._syncFs);
      document.removeEventListener("MSFullscreenChange", this._syncFs);
    }
  },
  methods: {
    colorCard(status) { return STATUS_COLORS[status] || STATUS_COLORS[0]; },
    activeChipStyle(opcion) {
      const c = this.colorCard(opcion ?? 0);
      return { background: c + "15", color: c, borderColor: c + "50" };
    },
    contarFiltro(opcion) {
      if (opcion == null) return this.maquinas.length;
      return this.maquinas.filter((m) => m.status === opcion).length;
    },
    async extenderPantalla() {
      const el = this.$refs.fsRoot;
      if (!document.fullscreenElement) {
        await el.requestFullscreen?.();
        this.isFullscreen = true;
      } else {
        await document.exitFullscreen?.();
        this.isFullscreen = false;
      }
    },
    syncPageRotation() {
      if (this.pageRotateInterval) clearInterval(this.pageRotateInterval);
      this.pageRotateInterval = null;
      if (this.totalPages <= 1) return;
      this.pageRotateInterval = setInterval(() => {
        this.currentPage = (this.currentPage + 1) % this.totalPages;
      }, PAGE_ROTATE_MS);
    },
    hoyMX() {
      return new Intl.DateTimeFormat("en-CA", {
        timeZone: "America/Mexico_City",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).format(new Date());
    },
    minutosHoyMX() {
      const [hh, mm] = new Intl.DateTimeFormat("en-GB", {
        timeZone: "America/Mexico_City",
        hour12: false,
        hour: "2-digit",
        minute: "2-digit",
      }).format(new Date()).split(":").map(Number);
      return hh * 60 + mm;
    },
    timeToMin(hms = "00:00:00") {
      const [hh = "00", mm = "00"] = String(hms).split(":");
      return Number(hh) * 60 + Number(mm);
    },
    rangoMinutos(m, start, end) {
      return start <= end ? m >= start && m < end : m >= start || m < end;
    },
    turnoActualId() {
      const m = this.minutosHoyMX();
      if (Array.isArray(this.turnos) && this.turnos.length) {
        for (const t of this.turnos) {
          if (this.rangoMinutos(m, this.timeToMin(t.inicio), this.timeToMin(t.fin))) return Number(t.idTurno);
        }
        const ordered = this.turnos
          .map((t) => ({ id: Number(t.idTurno), start: this.timeToMin(t.inicio) }))
          .sort((a, b) => a.start - b.start);
        const last = ordered.filter((x) => x.start <= m).pop() || ordered[ordered.length - 1];
        if (last) return last.id;
      }
      return this.rangoMinutos(m, 19 * 60, 7 * 60) ? 1 : 2;
    },
    buildParams(idMaquinaIns) {
      return {
        idMaquinaIns,
        fechaRev: this.hoyMX(),
        idTurno: this.turnoActualId(),
        sinDisponibilidad: this.sinDisponibilidad,
        desConParosNP: this.desConParosNP,
      };
    },
    toPct(v) {
      const n = Number(v);
      return Number.isFinite(n) ? (n <= 1 ? n * 100 : n) : null;
    },
    getAreas() {
      return axios.get("/Areas")
        .then((r) => { this.areas = (r.data || []).filter((a) => a.idArea > 1); })
        .catch((e) => console.log(e));
    },
    async getInstancias() {
      try {
        const res = await axios.get("/MaquinaInstancias");
        this.instancias = Array.isArray(res.data) ? res.data : [];
      } catch (e) {
        console.log(e);
        this.instancias = [];
      }
    },
    async getTurnos() {
      try {
        const { data } = await axios.get("/Turnos");
        this.turnos = Array.isArray(data) ? data : [];
      } catch (e) {
        console.log(e);
        this.turnos = [];
      }
    },
    buildInactiveMachine(instancia, raw) {
      const resumen = raw?.resumen || {};
      return {
        idMaquinaIns: instancia?.idMaquinaIns ?? raw?.idMaquinaIns ?? null,
        codigoMaquina: resumen.nombreMaquina || resumen.codigoMaquina || instancia?.nombre || instancia?.codigo || "Maquina",
        status: 0,
        oee: null,
        disponibilidad: null,
        rendimiento: null,
        calidad: null,
        pizasOk: 0,
        planeacionPorTurno: 0,
        scrapPct: null,
        piezasScrap: 0,
        tcRealSegPza: 0,
        tcRealTxt: "—",
        tcQadSegPza: 0,
        tcQadTxt: "—",
        tiempoParoSegundos: 0,
        inicioParo: null,
        paroTipo: null,
        paroSubTipo: null,
        fechaInicio: null,
        _segundosInicialesParo: 0,
      };
    },
    adaptEPResumen(ep, instancia) {
      const r = ep?.resumen || {};
      const ordenes = Array.isArray(ep?.ordenes) ? ep.ordenes : [];
      const cavRes = r?.resumenCavidades || {};
      const cavs = Array.isArray(cavRes.cavidades) ? cavRes.cavidades : [];
      const piezasOk = Number(cavRes?.piezasOkTotales ?? cavs.reduce((a, c) => a + (Number(c?.piezasOk) || 0), 0)) || 0;
      const piezasScrap = Number(cavRes?.piezasNoOkTotales ?? cavs.reduce((a, c) => a + (Number(c?.piezasNoOk) || 0), 0)) || 0;
      const booly = (v) => v === true || v === 1 || v === "1";
      const enParo = booly(r.hayParoEnCurso ?? r.HayParoEnCurso);
      const paroProg = booly(r.hayParoProgramadoEnCurso ?? r.HayParoProgramadoEnCurso);
      const paroNP = booly(r.hayParoNoProgramadoEnCurso ?? r.HayParoNoProgramadoEnCurso);
      const tcQadSegPza = ordenes.length > 0 ? Number(ordenes[0].tiempoCicloSeg ?? 0) : 0;
      const planeacionPorTurno = Number(r.piezasTeoricasTotales ?? 0);
      const totalPiezas = piezasOk + piezasScrap;
      const scrapPct = totalPiezas > 0 ? (piezasScrap / totalPiezas) * 100 : null;
      const hasData = enParo || ordenes.length > 0 || piezasOk > 0 || piezasScrap > 0 || planeacionPorTurno > 0 || tcQadSegPza > 0;
      if (!hasData) return this.buildInactiveMachine(instancia, ep);
      const tiempoParoSegundos = paroProg ? Number(r.segundosTiempoParoProgramado ?? 0) : Number(r.segundosTiempoParo ?? 0);
      return {
        idMaquinaIns: ep.idMaquinaIns ?? instancia?.idMaquinaIns ?? null,
        codigoMaquina: r.nombreMaquina || r.codigoMaquina || ep.nombreMaquina || instancia?.nombre || instancia?.codigo || "",
        status: enParo ? (paroNP ? 4 : 3) : 1,
        oee: this.toPct(r.oee),
        disponibilidad: this.toPct(r.disponibilidad),
        rendimiento: this.toPct(r.rendimiento),
        calidad: this.toPct(r.calidad),
        pizasOk: piezasOk,
        planeacionPorTurno,
        scrapPct,
        piezasScrap,
        tcRealSegPza: 0,
        tcRealTxt: "—",
        tcQadSegPza,
        tcQadTxt: tcQadSegPza > 0 ? `${tcQadSegPza.toFixed(1)} seg` : "—",
        tiempoParoSegundos,
        _segundosInicialesParo: tiempoParoSegundos,
        inicioParo: null,
        paroTipo: null,
        paroSubTipo: null,
        fechaInicio: null,
      };
    },
    async obtenerDatos() {
      if (!this.visible) return;
      if (this.loading) return;
      this.loading = true;
      try {
        const instanciasFiltradas = (this.instancias || []).filter((x) => this.idArea > 1 ? x.idArea === this.idArea : true);
        const ids = instanciasFiltradas.map((x) => x.idMaquinaIns).filter(Boolean);
        if (!ids.length) { this.maquinas = []; return; }
        const byId = Object.create(null);
        instanciasFiltradas.forEach((inst) => { byId[inst.idMaquinaIns] = inst; });
        const settled = await Promise.allSettled(
          ids.map((id) => axios.get("/OEECalculos/HoraPorHora", { params: this.buildParams(id) }))
        );
        this.maquinas = settled.map((result, idx) => {
          const id = ids[idx];
          const instancia = byId[id];
          if (result.status === "fulfilled" && result.value?.data) {
            const raw = result.value.data;
            raw.idMaquinaIns = raw.idMaquinaIns ?? id;
            return this.adaptEPResumen(raw, instancia);
          }
          return this.buildInactiveMachine(instancia, { idMaquinaIns: id });
        });
      } catch (e) {
        console.log(e);
      } finally {
        this.loading = false;
      }
    },
    async obtenerTokensMaquina(idMaquinaIns) {
      if (this.tokensCache[idMaquinaIns]) return this.tokensCache[idMaquinaIns];
      try {
        const { data } = await axios.get("/SensorDatos/TokensPorMaquina", { params: { idMaquinaIns } });
        const tokens = Array.isArray(data) ? data : [];
        if (tokens.length) this.tokensCache[idMaquinaIns] = tokens;
        return tokens;
      } catch (e) {
        console.error(`Error tokens maquina ${idMaquinaIns}:`, e);
        return [];
      }
    },
    async obtenerTcRealtimeMaquina(maquina) {
      if (!this.visible) return;
      if (!maquina || maquina.status !== 1) return;
      const tokens = await this.obtenerTokensMaquina(maquina.idMaquinaIns);
      if (!tokens.length) return;
      try {
        const params = {
          inicio: moment().subtract(5, "minutes").format("YYYY-MM-DD HH:mm:ss"),
          fin: moment().format("YYYY-MM-DD HH:mm:ss"),
          idMaquinaIns: maquina.idMaquinaIns,
          key: tokens[0],
        };
        const resp = await axios.get("/SensorDatos/SeriesRealtime", { params });
        const series = resp.data?.series || [];
        if (series.length && Array.isArray(series[0].data) && series[0].data.length) {
          const tc = Number(series[0].data[series[0].data.length - 1].y);
          if (Number.isFinite(tc) && tc > 0) {
            maquina.tcRealSegPza = tc;
            maquina.tcRealTxt = `${tc.toFixed(1)} seg`;
          }
        }
      } catch (e) {
        console.error(`Error TC realtime maquina ${maquina.idMaquinaIns}:`, e);
      }
    },
    async obtenerTcRealtimeActivasVisibles() {
      if (!this.maquinasActivasVisibles.length) return;
      await Promise.allSettled(this.maquinasActivasVisibles.map((m) => this.obtenerTcRealtimeMaquina(m)));
    },
    async refreshDashboard() {
      if (!this.visible) return;
      await this.obtenerDatos();
      await this.obtenerTcRealtimeActivasVisibles();
    },
    async iniciar() {
      await this.getInstancias();
      await this.getTurnos();
      await this.getAreas();
      await this.refreshDashboard();
      if (!this.interval) this.interval = setInterval(() => this.refreshDashboard(), DASH_REFRESH_MS);
      this.syncPageRotation();
    },
    detener() {
      if (this.interval) clearInterval(this.interval);
      if (this.pageRotateInterval) clearInterval(this.pageRotateInterval);
      this.interval = null;
      this.pageRotateInterval = null;
    },
  },
};
</script>

<style scoped>
.maquina-estado-v1-root{--clr-surface:#eef3f7;--clr-border:rgba(15,23,42,.08);--clr-text-primary:#0f172a;--clr-text-secondary:#475569;--font-primary:"Space Grotesk","Roboto",sans-serif;background:radial-gradient(circle at top left,rgba(15,95,151,.08),transparent 24%),linear-gradient(180deg,#f7fafc 0%,#edf2f7 100%);min-height:100%}
.toolbar-row{display:flex;align-items:center;justify-content:space-between;padding:16px 20px 12px;gap:12px}
.toolbar-left{display:flex;align-items:center;gap:10px;flex-wrap:wrap}
.toolbar-title{display:flex;align-items:center;gap:10px;font-family:var(--font-primary);font-size:19px;font-weight:800;color:var(--clr-text-primary);padding:6px 14px;border-radius:14px;background:linear-gradient(180deg,rgba(255,255,255,.96),rgba(241,247,252,.94));box-shadow:0 10px 26px rgba(15,23,42,.08)}
.machine-count-pill,.page-pill{display:inline-flex;align-items:center;padding:4px 11px;border-radius:999px;background:rgba(255,255,255,.9);border:1px solid var(--clr-border);font-family:var(--font-primary);font-size:11px;font-weight:700;color:var(--clr-text-secondary)}
.page-pill{color:#0f5f97}
.fs-btn{display:inline-flex;align-items:center;padding:8px 14px;border-radius:10px;border:1px solid rgba(15,95,151,.18);background:#fff;font-family:var(--font-primary);font-size:12px;font-weight:700;color:#0f5f97;cursor:pointer;box-shadow:0 8px 22px rgba(15,23,42,.08)}
.dashboard-card.v-card{background:transparent!important;border:none!important;box-shadow:none!important}
.dashboard-body.v-card__text{padding:0 10px 24px!important}
.area-select.v-input{background:rgba(255,255,255,.95);border-radius:12px}
.filter-chips{display:flex;flex-wrap:wrap;gap:8px;align-items:center}
.filter-chip{display:inline-flex;align-items:center;gap:6px;padding:7px 14px;border-radius:999px;border:1px solid var(--clr-border);background:#fff;font-family:var(--font-primary);font-size:12px;font-weight:600;color:var(--clr-text-secondary);cursor:pointer}
.filter-chip.active .chip-count{background:currentColor;color:#fff}
.chip-dot{width:8px;height:8px;border-radius:50%}
.chip-count{display:inline-flex;align-items:center;justify-content:center;min-width:20px;height:18px;padding:0 5px;border-radius:999px;background:var(--clr-surface);font-size:10px;font-weight:700}
.loading-state,.empty-state{display:flex;flex-direction:column;align-items:center;justify-content:center;padding:80px 20px;gap:14px;color:var(--clr-text-secondary);font-family:var(--font-primary)}
.loading-text,.empty-state p{margin:0}
.loading-bottom{margin-top:16px}
.maquina-estado-v1-root:fullscreen{padding:20px;overflow-y:auto;height:100vh}
@media (max-width:600px){.toolbar-row{padding:12px 14px 8px}.toolbar-title{font-size:14px;padding:6px 10px}.fs-btn-label{display:none}.fs-btn{padding:8px 10px}.dashboard-body.v-card__text{padding:0 6px 16px!important}}
@media (min-width:1264px){.dashboard-body.v-card__text{padding:0 4px 20px!important}}
</style>
